import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaLink, FaInbox } from 'react-icons/fa';
import EventCard from '../../../components/misc/EventCard';
import DownloadButton from '../../../components/misc/DownloadButton';
import OpenActionsCard from '../../../components/misc/OpenActionsCard';
import { getMyUpcomingEvents } from '../../../services/eventService';
import * as communityService from '../../../services/communityService';
import * as connectionService from '../../../services/connectionService';
import { getInboxInfo, InboxInfo } from '../../../services/notificationService';
import { PersonalEvent } from '../../../types/event';
import { CommunityData } from '../../../types/misc';
import useAuth from '../../../hooks/useAuth';
import { setSelectedCommunity } from '../../../redux/slices/applicationSlice';

type RecentCommunitiesProps = {
  communities: CommunityData[];
  communityLoading: boolean;
};

function RecentCommunities({ communities, communityLoading }: RecentCommunitiesProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  return (
    <div className="rounded-xl border">
      <div className="flex items-center justify-between rounded-t-xl bg-secondary-200 px-4 py-2">
        <div>
          <h3 className="text-secondary-900 font-semibold">
            {t('page.dashboard.recentCommunities') || 'Recent communities'}
          </h3>
        </div>
        <p
          onClick={() => navigate('/community')}
          className="flex cursor-pointer items-center gap-2 text-sm font-medium text-details/60">
          {t('general.view')}
        </p>
      </div>

      <div className="p-3">
        {communityLoading ? (
          <p className="text-secondary-500 text-sm">{t('general.loading')}</p>
        ) : communities.length === 0 ? (
          <p className="text-secondary-500 text-sm">{t('general.noResults')}</p>
        ) : (
          <div className="flex flex-col">
            {communities.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  dispatch(setSelectedCommunity(c.id));
                  navigate('/community');
                }}
                className="flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors hover:bg-secondary-50">
                {c.picture ? (
                  <img src={c.picture} alt={c.name} className="h-12 w-12 flex-shrink-0 rounded-md object-cover" />
                ) : (
                  <div className="text-secondary-500 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md bg-secondary-200 text-sm">
                    {c.name?.charAt(0) || '?'}
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{c.name}</p>
                  <p className="truncate text-xs text-details/80">
                    {c.memberCount} {t('page.dashboard.community.members') || 'members'}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCards({
  communitiesCount,
  communityLoading,
  connectionsCount,
  countsLoading,
  openActionsCount,
  onNavigate,
}: {
  communitiesCount: number;
  communityLoading: boolean;
  connectionsCount: number;
  countsLoading: boolean;
  openActionsCount: number;
  onNavigate: (path: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="mt-1 flex items-start gap-4">
      <button
        type="button"
        onClick={() => onNavigate('/communities')}
        className="flex-1 rounded-xl bg-secondary-50 px-6 py-8 text-left">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg text-primary">{t('page.dashboard.stats.communities')}</p>
            <p className="text-3xl font-bold leading-none">{communityLoading ? '—' : communitiesCount}</p>
          </div>

          <div className="ml-3 flex h-10 w-10 items-center justify-center rounded-md bg-secondary-200 text-primary-900">
            <FaUsers className="h-5 w-5" />
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={() => onNavigate('/connections')}
        className="flex-1 rounded-xl bg-secondary-50 px-6 py-8 text-left">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg text-primary">{t('page.dashboard.stats.connections')}</p>
            <p className="text-3xl font-bold leading-none">{countsLoading ? '—' : connectionsCount}</p>
          </div>

          <div className="ml-3 flex h-10 w-10 items-center justify-center rounded-md bg-secondary-200 text-primary-900">
            <FaLink className="h-5 w-5" />
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={() => onNavigate('/inbox')}
        className="hover:bg-secondary-100 flex-1 rounded-xl bg-secondary-50 px-6 py-8 text-left transition-colors">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg text-primary">{t('page.dashboard.stats.openActions')}</p>
            <p className="text-3xl font-bold leading-none">{countsLoading ? '—' : openActionsCount}</p>
          </div>

          <div className="ml-3 flex h-10 w-10 items-center justify-center rounded-md bg-secondary-200 text-primary-900">
            <FaInbox className="h-5 w-5" />
          </div>
        </div>
      </button>
    </div>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const displayName = auth?.alias ? auth.alias.replace('+', ' ') : '';
  const hour = new Date().getHours();
  const greetingKey =
    hour < 12
      ? 'page.dashboard.greeting.morning'
      : hour < 18
        ? 'page.dashboard.greeting.afternoon'
        : 'page.dashboard.greeting.evening';

  const [events, setEvents] = useState<PersonalEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [communities, setCommunities] = useState<CommunityData[]>([]);
  const [communityLoading, setCommunityLoading] = useState<boolean>(false);

  const [communitiesCount, setCommunitiesCount] = useState<number>(0);
  const [connectionsCount, setConnectionsCount] = useState<number>(0);
  const [openActionsCount, setOpenActionsCount] = useState<number>(0);
  const [countsLoading, setCountsLoading] = useState<boolean>(false);

  const [inboxInfo, setInboxInfo] = useState<InboxInfo | null>(null);

  useEffect(() => {
    setLoading(true);
    getMyUpcomingEvents()
      .then((data: PersonalEvent[]) => setEvents(data))
      .catch((err: unknown) => console.error('Failed to load upcoming events:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCountsLoading(true);

    connectionService
      .getMyConnections()
      .then((data) => {
        if (!cancelled) setConnectionsCount(Array.isArray(data) ? data.length : 0);
      })
      .catch((err: unknown) => console.error('Failed to load connections count:', err))
      .finally(() => {
        if (!cancelled) setCountsLoading(false);
      });

    // Inbox / open actions
    getInboxInfo()
      .then((info) => {
        if (cancelled) return;
        if (!cancelled) setInboxInfo(info);
        const collect = (items: any[] | undefined) => (Array.isArray(items) ? items : []);
        const arr = [
          ...collect(info.introductions),
          ...collect(info.dataRequests),
          ...collect(info.communityRequests),
          ...collect(info.connectionInvites),
          ...collect(info.eventInvites),
          ...collect(info.communityInvites),
          ...collect(info.tasks),
          ...collect(info.communityChangedDataRequests),
          ...collect(info.paymentRequests),
        ];
        setOpenActionsCount(arr.length);
      })
      .catch((err: unknown) => console.error('Failed to load inbox info:', err));

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCommunityLoading(true);

    communityService
      .getMyCommunities()
      .then((data) => {
        if (!cancelled) {
          setCommunities(Array.isArray(data) ? data.slice(0, 5) : []);
          setCommunitiesCount(Array.isArray(data) ? data.length : 0);
        }
      })
      .catch((err: unknown) => console.error('Failed to load communities:', err))
      .finally(() => {
        if (!cancelled) setCommunityLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      <div className="mx-auto flex w-full flex-col gap-6 px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <h1 className="mb-2 text-2xl font-bold text-primary sm:text-3xl">{t(greetingKey, { name: displayName })}</h1>
        </div>

        {/* Page content */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:gap-6">
              {/* Left: Events */}
              <div className="flex w-full min-w-0 flex-col gap-6 xl:basis-[75%]">
                {/* Small stat boxes (hidden on mobile, visible on md+) */}
                <div className="hidden md:block">
                  <StatCards
                    communitiesCount={communitiesCount}
                    communityLoading={communityLoading}
                    connectionsCount={connectionsCount}
                    countsLoading={countsLoading}
                    openActionsCount={openActionsCount}
                    onNavigate={(p: string) => navigate(p)}
                  />
                </div>
                <div className="rounded-xl border">
                  <div className="flex items-center justify-between rounded-t-xl bg-secondary-200 px-4 py-2">
                    <div>
                      <h3 className="font-semibold">{t('page.dashboard.events.upcoming')}</h3>
                    </div>
                    <p
                      onClick={() => navigate('/agenda')}
                      className="flex cursor-pointer items-center gap-2 text-sm font-medium text-details/60">
                      {t('general.view')}
                    </p>
                  </div>

                  <div className="p-4">
                    {loading ? (
                      <div className="text-secondary-500 py-6 text-center">
                        <p className="text-sm">{t('general.loading')}</p>
                      </div>
                    ) : events.length === 0 ? (
                      <div className="text-secondary-500 py-6 text-center">
                        <p className="text-sm">{t('page.dashboard.events.noEvents') || t('general.noResults')}</p>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {events.slice(0, 5).map((event) => (
                          <EventCard key={event.id} event={event as any} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="w-full">
                  <OpenActionsCard inboxInfo={inboxInfo} />
                </div>
              </div>

              {/* Right column: Recent communities + Download card */}
              <div className="flex w-full min-w-0 gap-4 xl:basis-[25%] xl:flex-col">
                <div className="hidden md:block">
                  <RecentCommunities communities={communities} communityLoading={communityLoading} />
                </div>

                <div className="rounded-xl border">
                  <div className="rounded-t-xl bg-secondary-200 px-4 py-3">
                    <h3 className="text-secondary-900 font-semibold">{t('page.dashboard.getMobileApp')}</h3>
                  </div>
                  <div className="overflow-hidden rounded-b-xl p-4">
                    <p className="text-secondary-600 mb-4 text-sm">
                      {t('download.variants.c.subheading') || t('common.message')}
                    </p>
                    <DownloadButton />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
